import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import Select from 'react-select'; 
import '../../style/EditPetPage.css';
import dogBreeds from '../../data/dogBreeds';
import catBreeds from '../../data/catBreeds';
import rabbitBreeds from '../../data/rabbitBreeds';
import behaviorWithDogs from '../../data/behaviorWithDogs';
import behaviorWithCats from '../../data/behaviorWithCats';
import behaviorWithPeople from '../../data/behaviorWithPeople';
import colors from '../../data/colors';
import eyeColors from '../../data/eyeColors';
import sexOptions from '../../data/sexOptions';
import sizeOptions from '../../data/sizeOptions';
import furAmountOptions from '../../data/furAmountOptions'; 
import furLengthOptions from '../../data/furLengthOptions'; 
import petSpecies from '../../data/petSpecies'; 
import { useTranslation } from 'react-i18next'; 
import i18n from '../../i18n'; // Import i18n instance



const EditPetPage = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // Get the pet ID from the URL
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation(); 
    const [step, setStep] = useState(1); // Track the current step
    const [petData, setPetData] = useState({
        name: '',
        petType: '',
        status: 'normal',
        description: '',
        general: {
            sex: '',
            birthDate: '',
            size: 'medium',
            breeds: [],
            primaryColor: '',
            secondaryColors: [],
            eyeColors: [],
            fur: {
                hasFur: true,
                amount: 'normal',
                length: 'medium',
            },
        },
        health: {
            castrated: false,
            hasChip: false,
            chipNumber: '',
            hasTattoo: false,
        },
    });

    const [loading, setLoading] = useState(false);

    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // Fetch the existing pet data when the component loads
    useEffect(() => {
        const fetchPet = async () => {
            console.log('fetchPet function is being executed'); 
            setLoading(true); // Start loading
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;
    
                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }
    
                const response = await axios.get(`${baseUrl}/api/pets/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
    
                const fetchedPetData = response.data;
                console.log('Fetched Pet Data:', fetchedPetData);
    
                // Format birthDate to yyyy-MM-dd
                if (fetchedPetData.general.birthDate) {
                    fetchedPetData.general.birthDate = new Date(fetchedPetData.general.birthDate)
                        .toISOString()
                        .split('T')[0]; // Extract only the date part
                }
    
                // Transform breeds to include label and value
                if (fetchedPetData.general.breeds) {
                    const breedOptions =
                        fetchedPetData.petType === 'dog'
                            ? dogBreeds
                            : fetchedPetData.petType === 'cat'
                            ? catBreeds
                            : fetchedPetData.petType === 'rabbit'
                            ? rabbitBreeds
                            : [];
    
                    fetchedPetData.general.breeds = fetchedPetData.general.breeds.map((breed) => {
                        const matchingBreed = breedOptions.find((b) => b.value === breed);
                        return {
                            label: matchingBreed ? getTranslatedLabel(breedOptions, breed) : breed, // Use `getTranslatedLabel` to get the correct label
                            value: breed, // Keep the original value
                        };
                    });
                }
                console.log('Final Pet Data Before Setting:', fetchedPetData);
                setPetData(fetchedPetData); // Populate the form with the existing pet data
            } catch (error) {
                console.error('Error in fetchPet:', error);
                enqueueSnackbar(t('generalPet.fetchError'), { variant: 'error' }); // Use translation for error
            } finally {
                setLoading(false); // Stop loading
            }
        };
    
        fetchPet();
    }, [id, enqueueSnackbar, baseUrl, t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPetData({ ...petData, [name]: value });
    };

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;
    
        if (name === 'petType') {
            // Update petType directly at the top level
            setPetData({
                ...petData,
                [name]: value,
            });
        } else {
            // Update fields inside the general object
            setPetData({
                ...petData,
                general: { ...petData.general, [name]: value },
            });
        }
    };

    const getTranslatedLabel = (options, value) => {
        if (!value) {
            console.log('Value is empty, returning unknown.');
            return t('general.unknown'); // Return "unknown" if value is empty
        }
    
        console.log('Options:', options);
        console.log('Value:', value);
        const normalizedValue = value.trim().toLowerCase(); // Normalize the value
        const option = options.find((o) => o.value === normalizedValue);
        console.log('Normalized Value:', normalizedValue);
        console.log('Found Option:', option);
        console.log('Current Language:', i18n.language);
        return option ? option.label[i18n.language] : t('general.unknown'); // Use translation for "unknown"
    };

    const handleSecondaryColorsChange = (e) => {
        const { value, checked } = e.target; // Extract value and checked state from the event
        setPetData((prevState) => {
            const updatedSecondaryColors = checked
                ? [...prevState.general.secondaryColors, value] // Add the color if checked
                : prevState.general.secondaryColors.filter((color) => color !== value); // Remove the color if unchecked
            return {
                ...prevState,
                general: { ...prevState.general, secondaryColors: updatedSecondaryColors },
            };
        });
    };

    const handleFurChange = (e) => {
        const { name, value } = e.target;
        const booleanValue = value === 'true'; // Convert string to boolean
        setPetData((prevState) => ({
            ...prevState,
            general: {
                ...prevState.general,
                fur: {
                    ...prevState.general.fur,
                    [name]: name === 'hasFur' ? booleanValue : value, // Store boolean for hasFur
                    ...(name === 'hasFur' && !booleanValue
                        ? { amount: 'none', length: 'none' } // Clear `amount` and `length` if `hasFur` is false
                        : {}),
                },
            },
        }));
    };

    const handleEyeColorsChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedEyeColors = checked
                ? [...prevState.general.eyeColors, value]
                : prevState.general.eyeColors.filter((color) => color !== value);
            return {
                ...prevState,
                general: { ...prevState.general, eyeColors: updatedEyeColors },
            };
        });
    };

    const handleWithPeopleChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedWithPeople = checked
                ? [...prevState.behavior.withPeople, value]
                : prevState.behavior.withPeople.filter((behavior) => behavior !== value);
            return {
                ...prevState,
                behavior: { ...prevState.behavior, withPeople: updatedWithPeople },
            };
        });
    };

    const handleWithDogsChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedWithDogs = checked
                ? [...prevState.behavior.withDogs, value]
                : prevState.behavior.withDogs.filter((behavior) => behavior !== value);
            return {
                ...prevState,
                behavior: { ...prevState.behavior, withDogs: updatedWithDogs },
            };
        });
    };
    
    const handleWithCatsChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedWithCats = checked
                ? [...prevState.behavior.withCats, value]
                : prevState.behavior.withCats.filter((behavior) => behavior !== value);
            return {
                ...prevState,
                behavior: { ...prevState.behavior, withCats: updatedWithCats },
            };
        });
    };


    const handleBreedChange = (selectedOptions) => {
        setPetData((prevState) => ({
            ...prevState,
            general: {
                ...prevState.general,
                breeds: Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions], // Ensure breeds is always an array
            },
        }));
    };

    const handleMixedBreedChange = (e) => {
        const { checked } = e.target;
        console.log('Mixed Breed Checkbox:', checked); 
        setPetData((prevState) => ({
            ...prevState,
            general: { ...prevState.general, isMixedBreed: checked, breeds: [] }, // Reset breeds if mixedBreed changes
        }));
    };




    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
    
        try {
            // Validate required fields
            if (!petData.name.trim()) {
                enqueueSnackbar(t('generalPet.nameRequired'), { variant: 'error' }); // Translated error message
                setLoading(false);
                return;
            }
    
            if (!petData.petType || petData.petType === '') {
                enqueueSnackbar(t('generalPet.typeRequired'), { variant: 'error' }); // Translated error message
                setLoading(false);
                return;
            }
    
            const storedUser = JSON.parse(localStorage.getItem('user')); // Get the user from localStorage
            const token = storedUser?.token; // Extract the token
    
            if (!token) {
                throw new Error(t('general.noAuthToken')); // Translated error message
            }
    
            // Transform data before sending to the backend
            const transformedPetData = {
                ...petData,
                general: {
                    ...petData.general,
                    breeds: petData.general.breeds.map((breed) => breed.value || breed), // Ensure breeds are sent as values
                    fur: {
                        ...petData.general.fur,
                        amount: petData.general.fur.hasFur === 'false' ? 'none' : petData.general.fur.amount,
                        length: petData.general.fur.hasFur === 'false' ? 'none' : petData.general.fur.length,
                    },
                },
            };
    
            // Send the updated pet data to the backend
            const response = await axios.put(`${baseUrl}/api/pets/${id}`, transformedPetData, {
                headers: {
                    Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                    'Content-Type': 'application/json', // Send JSON data
                },
            });
    
            console.log('Update Response:', response.data); // Log the response to verify the update
            setLoading(false);
            enqueueSnackbar(t('generalPet.successMessageEdit'), { variant: 'success' }); // Translated success message
            navigate(`/pet/${id}`, { replace: true }); // Navigate back to the pet page and refresh
        } catch (error) {
            setLoading(false);
            console.error('Error updating pet:', error.response || error.message);
            enqueueSnackbar(t('generalPet.errorMessageEdit'), { variant: 'error' }); // Translated error message
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('editPetPage.confirmDelete'))) return; // Confirm deletion

        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken'));
            }

            await axios.delete(`${baseUrl}/api/pets/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            enqueueSnackbar(t('editPetPage.successDelete'), { variant: 'success' }); // Success message
            navigate(`/`, { replace: true }); // Navigate back to the pet list
        } catch (error) {
            console.error('Error deleting pet:', error.response || error.message);
            enqueueSnackbar(t('editPetPage.errorDelete'), { variant: 'error' }); // Error message
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {

        if (loading) {
            return <p>{t('general.loading')}</p>; // Show a loading message while fetching data
        }

        console.log('petData in renderStep:', petData); 

        switch (step) {
            case 1:
                
                console.log('petSpecies:', petSpecies);
                console.log('petData.petType:', petData.petType);

                console.log(
                    'Translated Pet Type:',
                    petData.petType
                        ? petSpecies.find((species) => species.value === petData.petType.trim().toLowerCase())?.label[i18n.language]
                        : t('general.unknown')
                );

                console.log('Current Language:', i18n.language);

                return (
                    <div className="form-step">
                        <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.step1Title')}</h3>
                        <div className="form-group">
                            <label htmlFor="name" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.petName')}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={petData.name}
                                onChange={handleInputChange}
                                required
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="petType" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.petType')}</label>
                            <select
                                id="petType"
                                name="petType"
                                value={petData.petType}
                                onChange={handleGeneralChange}
                                required
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value="">{t('general.selectOption')}</option>
                                {petSpecies.map((species) => (
                                    <option key={species.value} value={species.value}>
                                        {getTranslatedLabel(petSpecies, species.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="sex" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.gender')}</label>
                            <select
                                id="sex"
                                name="sex"
                                value={petData.general.sex}
                                onChange={handleGeneralChange}
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value="">{t('general.selectOption')}</option>
                                {sexOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {getTranslatedLabel(sexOptions, option.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="birthDate" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.birthDate')}</label>
                            <input
                                type="date"
                                id="birthDate"
                                name="birthDate"
                                value={petData.general.birthDate}
                                onChange={handleGeneralChange}
                                required
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="description" className="block text-dafauna-darkText font-medium mb-2">Moto</label>
                            <textarea
                                id="description"
                                name="description"
                                value={petData.description}
                                onChange={handleInputChange}
                                required
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <button
                            onClick={() => {
                                if (!petData.name.trim()) {
                                    enqueueSnackbar(t('generalPet.nameRequired'), { variant: 'error' });
                                    return;
                                }
                                if (!petData.petType || petData.petType === '') {
                                    enqueueSnackbar(t('generalPet.typeRequired'), { variant: 'error' });
                                    return;
                                }
                                if (!petData.general.sex || petData.general.sex === '') {
                                    enqueueSnackbar(t('generalPet.genderRequired'), { variant: 'error' });
                                    return;
                                }
                                if (!petData.general.birthDate) {
                                    enqueueSnackbar(t('generalPet.birthDateRequired'), { variant: 'error' });
                                    return;
                                }
                                setStep(2);
                            }}
                            className="btn next-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                        >
                            {t('general.next')}
                        </button>
                    </div>
                );
            case 2:
                return (
                    <div className="form-step">
                        <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.step2Title')}</h3>
                        <div className="form-group-mixed-breed flex items-center gap-2">
                            <label htmlFor="isMixedBreed" className="text-dafauna-darkText font-medium">{t('generalPet.isMixedBreed')}</label>
                            <input
                                type="checkbox"
                                id="isMixedBreed"
                                name="isMixedBreed"
                                checked={petData.general.isMixedBreed}
                                onChange={handleMixedBreedChange}
                                className="cursor-pointer"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="breeds" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.breedSelection')}</label>
                            <Select
                                id="breeds"
                                name="breeds"
                                isMulti={petData.general.isMixedBreed}
                                options={
                                    petData.petType === 'dog'
                                        ? dogBreeds.map((breed) => ({
                                            value: breed.value,
                                            label: getTranslatedLabel(dogBreeds, breed.value),
                                        }))
                                        : petData.petType === 'cat'
                                        ? catBreeds.map((breed) => ({
                                            value: breed.value,
                                            label: getTranslatedLabel(catBreeds, breed.value),
                                        }))
                                        : petData.petType === 'rabbit'
                                        ? rabbitBreeds.map((breed) => ({
                                            value: breed.value,
                                            label: getTranslatedLabel(rabbitBreeds, breed.value),
                                        }))
                                        : []
                                }
                                value={petData.general.breeds}
                                onChange={handleBreedChange}
                                placeholder={
                                    petData.general.isMixedBreed
                                        ? t('generalPet.selectBreedsOrUnknown')
                                        : t('generalPet.selectBreedOrUnknown')
                                }
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="size" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.size')}</label>
                            <select
                                id="size"
                                name="size"
                                value={petData.general.size}
                                onChange={handleGeneralChange}
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                {sizeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {getTranslatedLabel(sizeOptions, option.value)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="hasFur" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.hasFur')}</label>
                            <select
                                id="hasFur"
                                name="hasFur"
                                value={petData.general.fur.hasFur}
                                onChange={handleFurChange}
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value={true}>{t('general.yes')}</option>
                                <option value={false}>{t('general.no')}</option>
                            </select>
                        </div>

                        {petData.general.fur.hasFur && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="amount" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.furAmount')}</label>
                                    <select
                                        id="amount"
                                        name="amount"
                                        value={petData.general.fur.amount}
                                        onChange={handleFurChange}
                                        className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    >
                                        <option value="">{t('generalPet.selectAmount')}</option>
                                        {furAmountOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {getTranslatedLabel(furAmountOptions, option.value)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="length" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.furLength')}</label>
                                    <select
                                        id="length"
                                        name="length"
                                        value={petData.general.fur.length}
                                        onChange={handleFurChange}
                                        className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    >
                                        <option value="">{t('generalPet.selectLength')}</option>
                                        {furLengthOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {getTranslatedLabel(furLengthOptions, option.value)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}
                        <div className="form-group">
                            <label htmlFor="primaryColor" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.primaryColor')}</label>
                            <select
                                id="primaryColor"
                                name="primaryColor"
                                value={petData.general.primaryColor}
                                onChange={handleGeneralChange}
                                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value="">{t('generalPet.selectColor')}</option>
                                {colors.map((color) => (
                                    <option key={color.value} value={color.value}>
                                        {getTranslatedLabel(colors, color.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.secondaryColors')}</label>
                            <div className="secondary-colors flex flex-wrap gap-2">
                                {colors.map((color) => (
                                    <div key={color.value} className="color-box">
                                        <input
                                            type="checkbox"
                                            id={`color-${color.value}`}
                                            value={color.value}
                                            checked={petData.general.secondaryColors.includes(color.value)}
                                            onChange={handleSecondaryColorsChange}
                                            className="hidden"
                                        />
                                        <label htmlFor={`color-${color.value}`} className="block text-center">
                                            {getTranslatedLabel(colors, color.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.eyeColors')}</label>
                            <div className="eye-colors flex flex-wrap gap-2">
                                {eyeColors.map((color) => (
                                    <div key={color.value} className="color-box">
                                        <input
                                            type="checkbox"
                                            id={`eye-color-${color.value}`}
                                            value={color.value}
                                            checked={petData.general.eyeColors.includes(color.value)}
                                            onChange={handleEyeColorsChange}
                                            className="hidden"
                                        />
                                        <label htmlFor={`eye-color-${color.value}`} className="block text-center">
                                            {getTranslatedLabel(eyeColors, color.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="nav-edit-pet flex justify-between mt-4">
                            <button onClick={() => setStep(1)} className="btn back-btn bg-dafauna-gray text-white rounded-md px-4 py-2 hover:bg-dafauna-darkText">
                                {t('general.back')}
                            </button>
                            <button
                                onClick={() => {
                                    if (!petData.general.breeds || petData.general.breeds.length === 0) {
                                        enqueueSnackbar(t('generalPet.breedRequired'), { variant: 'error' });
                                        return;
                                    }
                                    if (petData.general.fur.hasFur && (!petData.general.fur.amount || !petData.general.fur.length)) {
                                        enqueueSnackbar(t('generalPet.furRequired'), { variant: 'error' });
                                        return;
                                    }
                                    setStep(3);
                                }}
                                className="btn next-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                            >
                                {t('general.next')}
                            </button>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="form-step">
                        <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.step3Title')}</h3>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.behaviorWithPeople')}</label>
                            <div className="behavior-with-people flex flex-wrap gap-2">
                                {behaviorWithPeople.map((behavior) => (
                                    <div key={behavior.value} className="behavior-box flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id={`behavior-people-${behavior.value}`}
                                            value={behavior.value}
                                            checked={petData.behavior.withPeople.includes(behavior.value)}
                                            onChange={handleWithPeopleChange}
                                            className="cursor-pointer"
                                        />
                                        <label htmlFor={`behavior-people-${behavior.value}`} className="text-dafauna-darkText">
                                            {getTranslatedLabel(behaviorWithPeople, behavior.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.behaviorWithDogs')}</label>
                            <div className="behavior-with-dogs flex flex-wrap gap-2">
                                {behaviorWithDogs.map((behavior) => (
                                    <div key={behavior.value} className="behavior-box flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id={`behavior-dogs-${behavior.value}`}
                                            value={behavior.value}
                                            checked={petData.behavior.withDogs.includes(behavior.value)}
                                            onChange={handleWithDogsChange}
                                            className="cursor-pointer"
                                        />
                                        <label htmlFor={`behavior-dogs-${behavior.value}`} className="text-dafauna-darkText">
                                            {getTranslatedLabel(behaviorWithDogs, behavior.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.behaviorWithCats')}</label>
                            <div className="behavior-with-cats flex flex-wrap gap-2">
                                {behaviorWithCats.map((behavior) => (
                                    <div key={behavior.value} className="behavior-box flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id={`behavior-cats-${behavior.value}`}
                                            value={behavior.value}
                                            checked={petData.behavior.withCats.includes(behavior.value)}
                                            onChange={handleWithCatsChange}
                                            className="cursor-pointer"
                                        />
                                        <label htmlFor={`behavior-cats-${behavior.value}`} className="text-dafauna-darkText">
                                            {getTranslatedLabel(behaviorWithCats, behavior.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="nav-edit-pet flex justify-between mt-4">
                            <button onClick={() => setStep(2)} className="btn back-btn bg-dafauna-gray text-white rounded-md px-4 py-2 hover:bg-dafauna-darkText">
                                {t('general.back')}
                            </button>
                            <button onClick={() => setStep(4)} className="btn next-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight">
                                {t('general.next')}
                            </button>
                        </div>
                    </div>
                );
            case 4:
                console.log('Selected petType:', petData);
                return (
                    <div className="form-step">
                        <h3 className="text-dafauna-darkText text-lg font-bold mb-4">{t('generalPet.step4Title')}</h3>

                        <div className="form-group-castrated flex items-center gap-2">
                            <label htmlFor="castrated" className="text-dafauna-darkText font-medium">{t('generalPet.isCastrated')}</label>
                            <input
                                type="checkbox"
                                id="castrated"
                                name="castrated"
                                checked={petData.health?.castrated || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: { ...prevState.health, castrated: checked },
                                    }));
                                }}
                                className="cursor-pointer"
                            />
                        </div>

                        <div className="form-group-chip flex items-center gap-2">
                            <label htmlFor="hasChip" className="text-dafauna-darkText font-medium">{t('generalPet.hasChip')}</label>
                            <input
                                type="checkbox"
                                id="hasChip"
                                name="hasChip"
                                checked={petData.health?.hasChip || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: { ...prevState.health, hasChip: checked, chipNumber: checked ? prevState.health?.chipNumber || '' : null },
                                    }));
                                }}
                                className="cursor-pointer"
                            />
                        </div>

                        {petData.health?.hasChip && (
                            <div className="form-group">
                                <label htmlFor="chipNumber" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.chipNumber')}</label>
                                <input
                                    type="text"
                                    id="chipNumber"
                                    name="chipNumber"
                                    value={petData.health?.chipNumber || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setPetData((prevState) => ({
                                            ...prevState,
                                            health: { ...prevState.health, chipNumber: value },
                                        }));
                                    }}
                                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                />
                            </div>
                        )}

                        <div className="form-group-tattoo flex items-center gap-2">
                            <label htmlFor="hasTattoo" className="text-dafauna-darkText font-medium">{t('generalPet.hasTattoo')}</label>
                            <input
                                type="checkbox"
                                id="hasTattoo"
                                name="hasTattoo"
                                checked={petData.health?.hasTattoo || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: { ...prevState.health, hasTattoo: checked },
                                    }));
                                }}
                                className="cursor-pointer"
                            />
                        </div>

                        <div className="nav-edit-pet flex justify-between mt-4">
                            <button onClick={() => setStep(3)} className="btn back-btn bg-dafauna-gray text-white rounded-md px-4 py-2 hover:bg-dafauna-darkText">
                                {t('general.back')}
                            </button>
                            <button onClick={handleSubmit} className="btn finish-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight" disabled={loading}>
                                {loading ? t('general.loading') : t('general.finish')}
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="edit-pet-container bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('editPetPage.title')}</h2>
            {renderStep()}
            <div className="delete-button-container mt-4">
                <button
                    onClick={handleDelete}
                    className="delete-button bg-red-500 text-white rounded-md px-4 py-2 hover:bg-red-600"
                    disabled={loading}
                >
                    {t('generalPet.deletePet')}
                </button>
            </div>
        </div>
    );
};

export default EditPetPage;